import type { UseQueryResult } from "@edreamsodigeo/frontend-api-client";

import useGetSubscriptionOffers from "~/clientState/hooks/useGetSubscriptionOffers";
import type {
  SubscriptionOffersQuery,
  SubscriptionOffersQueryVariables,
  SubscriptionResponse,
  SubscriptionOfferType,
  SubscriptionOfferPeriod,
} from "~/generated/graphql";
import { LoyaltyReward } from "~/generated/graphql";

export interface GetLoyaltyRewardFromOffer {
  isLoyaltyRewardProgramIncluded: boolean;
  hasLoyaltyRewards: boolean;
  offers: Array<SubscriptionResponse>;
  error: UseQueryResult<
    SubscriptionOffersQuery,
    SubscriptionOffersQueryVariables
  >["errors"];
}

export interface UseGetLoyaltyRewardFromOfferProps {
  offerPeriod?: SubscriptionOfferPeriod;
  offerType?: SubscriptionOfferType;
  loyaltyReward?: LoyaltyReward;
}

interface LoyaltyRewardIncludedProps {
  offers: Array<SubscriptionResponse>;
  loyaltyReward: LoyaltyReward;
}

export const hasAnyLoyaltyRewards = ({
  offers,
}: {
  offers: Array<SubscriptionResponse>;
}): boolean => {
  return offers.some(
    (offer) =>
      (offer.catalogueOffer?.plan?.membershipTier?.loyaltyRewardsProgram
        ?.loyaltyRewards?.length ?? 0) > 0,
  );
};

export const isLoyaltyRewardIncluded = ({
  offers,
  loyaltyReward,
}: LoyaltyRewardIncludedProps): boolean => {
  return offers.some(
    (offer) =>
      offer.catalogueOffer?.plan?.membershipTier?.loyaltyRewardsProgram?.loyaltyRewards?.some(
        (reward) => reward === loyaltyReward,
      ) ?? false,
  );
};

const filterOfferByType = ({ offers, type }): Array<SubscriptionResponse> =>
  offers.filter((offer: SubscriptionResponse) => offer.type === type);

const useGetLoyaltyRewardFromOffer = ({
  offerPeriod,
  offerType,
  loyaltyReward = LoyaltyReward.WelcomePromoCodeRewardThreeMonthsClaimable,
}: UseGetLoyaltyRewardFromOfferProps = {}): GetLoyaltyRewardFromOffer => {
  const { offers: offersList, error } = useGetSubscriptionOffers({
    offerPeriod,
  });

  const filteredOffers = offerType
    ? filterOfferByType({
        offers: offersList,
        type: offerType,
      })
    : offersList;

  const isLoyaltyRewardProgramIncluded = isLoyaltyRewardIncluded({
    offers: filteredOffers,
    loyaltyReward: loyaltyReward,
  });

  const hasLoyaltyRewards = hasAnyLoyaltyRewards({
    offers: filteredOffers,
  });

  return {
    isLoyaltyRewardProgramIncluded,
    hasLoyaltyRewards,
    offers: filteredOffers,
    error,
  };
};

export default useGetLoyaltyRewardFromOffer;
